import { useState, useEffect } from 'react'

const STATS_API_BASE_URL = import.meta.env.VITE_API_URL as string

export interface StatsGeneral {
  total_asistentes: number
  total_impresos: number
  total_pendientes: number
  porcentaje_impreso: number
  total_impresos_kiosko?: number
}

export interface StatsByDay {
  fecha: string
  cantidad: number
  asistentes_unicos: number
}

export interface StatsByHour {
  hora: number
  cantidad: number
}

export interface RecentPrint {
  id_asistente: number
  nombre: string
  apellido: string
  email: string
  fecha_impreso: string
  extra: Record<string, any>
}

export interface ExtraFieldStats {
  label: string
  stats: {
    total: Record<string, number>
    impresos: Record<string, number>
    pendientes: Record<string, number>
  }
}

export interface ActividadReciente {
  ultimas_24h: number
  horas_activas_24h: number
  hora_pico: {
    hora: number | null
    cantidad: number
  }
}

export interface StatsData {
  general: StatsGeneral
  por_dia: StatsByDay[]
  por_dia_visitante: StatsByDay[]
  por_dia_kiosko: StatsByDay[]
  por_hora_hoy: StatsByHour[]
  ultimas_impresiones: RecentPrint[]
  campos_extra: Record<string, ExtraFieldStats>
  actividad_reciente: ActividadReciente
}

export function useStats(autoRefresh = false, refreshInterval = 30000) {
  const [stats, setStats] = useState<StatsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)

  const fetchStats = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetch(`${STATS_API_BASE_URL}/api/v1/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.status !== 200) {
        throw new Error(result.message || 'Error al cargar estadísticas')
      }
      
      setStats(result.data)
      setLastUpdate(new Date())
    } catch (err) {
      console.error('Error fetching stats:', err)
      setError(err instanceof Error ? err.message : 'Error al cargar estadísticas')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      const interval = setInterval(fetchStats, refreshInterval)
      return () => clearInterval(interval)
    }
  }, [autoRefresh, refreshInterval])

  return {
    stats,
    loading,
    error,
    lastUpdate,
    refresh: fetchStats,
  }
}
